import { Button } from '@base-ui/react/button';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import { apiErrorDetail } from '@/api/errorDetail';
import type { components } from '@/api/qraft/schema';
import { ConfirmDialog } from '~/components/ui/ConfirmDialog';
import { CourseHeader } from '~/components/ui/ContentNav';
import { DetailPage } from '~/components/ui/Editor';
import { ErrorText } from '~/components/ui/Form';
import {
  type ChangeKind,
  ChangeMark,
  invalidateCourseVersions,
} from '~/components/ui/Versions';
import buttonStyles from '~/styles/button.module.css';

type Change = components['schemas']['Change'];
type ChangeSet = components['schemas']['ChangeSet'];
type Blocker = components['schemas']['Blocker'];

export const Route = createFileRoute(
  '/_admin/courses/$courseId/$version_/changes',
)({ component: ReviewChanges });

const ENTITY: Record<Change['entity'], string> = {
  course: 'Course',
  lesson: 'Lesson',
  topic: 'Topic',
  question: 'Question',
};

// Each button says what it does to this one change.
const UNDO: Record<ChangeKind, string> = {
  edited: 'Revert to live',
  removed: 'Restore',
  moved: 'Move back',
  added: 'Remove',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function ReviewChanges() {
  const { courseId, version } = Route.useParams();
  const course_id = Number(courseId);
  const versionNum = Number(version);
  const [publishing, setPublishing] = useState(false);

  const course = api.adminGetCourse.useQuery({
    path: { course_id },
    query: { version: versionNum },
  });
  const changes = api.adminCourseVersionChanges.useQuery({
    path: { course_id, version: versionNum },
  });

  if (course.isLoading || changes.isLoading)
    return <Message>Loading changes…</Message>;
  if (!course.data || !changes.data)
    return (
      <Message $error>
        {apiErrorDetail(changes.error) ?? 'Could not load the changes.'}
      </Message>
    );

  const set = changes.data;
  return (
    <DetailPage>
      <CourseHeader course={course.data} />
      <Heading>
        <h2>Changes in draft v{versionNum}</h2>
        <span>Compared with live v{set.base_version}</span>
      </Heading>
      <Summary set={set} />
      {set.changes.length === 0 ? (
        <Empty>
          No changes against live yet. Edit anything in this draft and it shows
          up here.
        </Empty>
      ) : (
        <Groups
          changes={set.changes}
          courseId={course_id}
          version={versionNum}
        />
      )}
      <Unchanged>
        {plural(set.unchanged_lessons, 'lesson')},{' '}
        {plural(set.unchanged_topics, 'topic')} and{' '}
        {plural(set.unchanged_questions, 'question')} unchanged
      </Unchanged>
      <StickyBar>
        <span>
          Publishing makes v{versionNum} the version new students start. Nothing
          changes for students already on an earlier version.
        </span>
        <Button
          className={`${buttonStyles.Button} ${buttonStyles.Primary}`}
          onClick={() => setPublishing(true)}
        >
          Publish v{versionNum}…
        </Button>
      </StickyBar>
      {publishing && (
        <PublishDialog
          courseId={course_id}
          version={versionNum}
          title={course.data.title}
          onClose={() => setPublishing(false)}
        />
      )}
    </DetailPage>
  );
}

function Summary({ set }: { set: ChangeSet }) {
  const chips: [ChangeKind, string][] = [
    ['added', 'added'],
    ['edited', 'edited'],
    ['moved', 'moved'],
    ['removed', 'removed'],
  ];
  return (
    <Chips aria-label="Summary">
      {chips
        .filter(([kind]) => set.counts[kind] > 0)
        .map(([kind, word]) => (
          <Chip key={kind} $kind={kind}>
            <ChangeMark kind={kind} /> {set.counts[kind]} {word}
          </Chip>
        ))}
    </Chips>
  );
}

// Consecutive changes under one lesson share a heading; the list is already in
// outline order.
function Groups({
  changes,
  courseId,
  version,
}: {
  changes: Change[];
  courseId: number;
  version: number;
}) {
  const groups: { key: string; title: string; changes: Change[] }[] = [];
  for (const change of changes) {
    const title =
      change.entity === 'course'
        ? 'Course'
        : change.entity === 'lesson'
          ? change.title
          : (change.lesson?.title ?? '');
    const key = `${change.entity === 'lesson' ? change.lineage_id : change.lesson?.lineage_id}`;
    const last = groups.at(-1);
    if (last && last.key === key && change.entity !== 'course')
      last.changes.push(change);
    else groups.push({ key, title, changes: [change] });
  }
  return (
    <GroupList>
      {groups.map((group, i) => (
        <Group key={`${group.key}-${i}`}>
          <GroupTitle>{group.title}</GroupTitle>
          {group.changes.map((change) => (
            <ChangeRow
              key={`${change.entity}-${change.lineage_id}-${change.kind}`}
              change={change}
              courseId={courseId}
              version={version}
            />
          ))}
        </Group>
      ))}
    </GroupList>
  );
}

function describe(change: Change): string {
  const what = ENTITY[change.entity];
  switch (change.kind) {
    case 'added':
      return `${what} · new${change.contains ? ` · ${change.contains}` : ''}`;
    case 'removed':
      return `${what} · removed${change.contains ? ` · ${change.contains}` : ''}`;
    case 'moved':
      return change.from_parent
        ? `${what} · moved here from ${change.from_parent.title}`
        : `${what} · moved from position ${change.from_position} to ${change.to_position}`;
    default: {
      const parts = [
        change.wording?.length && 'wording',
        change.settings?.length && 'settings',
        change.images?.length && 'images',
      ].filter(Boolean);
      const list =
        parts.length > 1
          ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`
          : parts[0];
      return `${what} · ${list}`;
    }
  }
}

function ItemLink({ change }: { change: Change }) {
  const id = change.draft_id;
  if (id === null) return <s>{change.title}</s>;
  const text = <strong>{change.title}</strong>;
  if (change.entity === 'lesson')
    return (
      <Link to="/lessons/$lessonId" params={{ lessonId: String(id) }}>
        {text}
      </Link>
    );
  if (change.entity === 'topic')
    return (
      <Link to="/topics/$topicId" params={{ topicId: String(id) }}>
        {text}
      </Link>
    );
  if (change.entity === 'question')
    return (
      <Link to="/questions/$questionId" params={{ questionId: String(id) }}>
        {text}
      </Link>
    );
  return text;
}

function ChangeRow({
  change,
  courseId,
  version,
}: {
  change: Change;
  courseId: number;
  version: number;
}) {
  const [confirming, setConfirming] = useState(false);
  const wording = change.wording ?? [];
  const settings = change.settings ?? [];
  const images = change.images ?? [];
  const revert = api.adminRevertCourseVersionChange.useMutation(undefined, {
    meta: { toast: 'Change undone' },
    onSuccess: (next) => {
      setConfirming(false);
      queryClient.setQueryData(
        api.adminCourseVersionChanges.getQueryKey({
          path: { course_id: courseId, version },
        }),
        next,
      );
      invalidateCourseVersions(courseId);
    },
  });
  const run = (attribute?: string) =>
    revert.mutate({
      path: { course_id: courseId, version },
      body: {
        entity: change.entity,
        lineage_id: change.lineage_id,
        kind: change.kind,
        ...(attribute ? { attribute } : {}),
      },
    });
  // With more than one field changed, each can be undone on its own. Questions
  // carry no attribute: they only revert whole.
  const perField =
    change.kind === 'edited' &&
    [...wording, ...settings, ...images].filter((f) => f.attribute).length > 1;
  const fieldUndo = (attribute: string | null | undefined, field: string) =>
    perField && attribute ? (
      <FieldUndo
        type="button"
        disabled={revert.isPending}
        aria-label={`Revert ${field} to live`}
        onClick={() => run(attribute)}
      >
        Revert
      </FieldUndo>
    ) : null;
  return (
    <Row>
      <RowHead>
        <RowTitle>
          <ChangeMark kind={change.kind} />
          <ItemLink change={change} />
          <Kind>{describe(change)}</Kind>
        </RowTitle>
        <Button
          className={buttonStyles.Button}
          disabled={revert.isPending}
          onClick={() =>
            change.kind === 'added' ? setConfirming(true) : run()
          }
        >
          {perField ? 'Revert all to live' : UNDO[change.kind]}
        </Button>
      </RowHead>
      {wording.map((w) => (
        <Diff key={w.field}>
          <DiffHead>
            <DiffLabel>{w.field}</DiffLabel>
            {fieldUndo(w.attribute, w.field)}
          </DiffHead>
          <p>
            {w.segments.map((s, i) =>
              s.op === 'insert' ? (
                <ins key={i}>{s.text}</ins>
              ) : s.op === 'delete' ? (
                <del key={i}>{s.text}</del>
              ) : (
                <span key={i}>{s.text}</span>
              ),
            )}
          </p>
        </Diff>
      ))}
      {settings.length > 0 && (
        <Settings>
          {settings.map((s, i) => (
            <span key={s.field}>
              {i > 0 && ' · '}
              {s.field}: <b>{s.before || '(none)'}</b> →{' '}
              <b>{s.after || '(none)'}</b>
              {fieldUndo(s.attribute, s.field)}
            </span>
          ))}
        </Settings>
      )}
      {images.map((image) => (
        <Diff key={image.field}>
          <DiffHead>
            <DiffLabel>{image.field}</DiffLabel>
            {fieldUndo(image.attribute, image.field)}
          </DiffHead>
          <ImagePair>
            <ImageSide label="Live" url={image.before} />
            <Arrow aria-hidden="true">→</Arrow>
            <ImageSide label="This draft" url={image.after} />
          </ImagePair>
        </Diff>
      ))}
      {revert.isError && (
        <ErrorText>
          {apiErrorDetail(revert.error) ?? 'Could not undo this change.'}
        </ErrorText>
      )}
      <ConfirmDialog
        open={confirming}
        title={`Remove ${change.title}?`}
        confirmLabel="Remove"
        tone="danger"
        pending={revert.isPending}
        onConfirm={() => run()}
        onCancel={() => setConfirming(false)}
      >
        <p>
          It's new in this draft, so removing it deletes what was written
          {change.contains ? `, including ${change.contains}` : ''}.
        </p>
      </ConfirmDialog>
    </Row>
  );
}

function ImageSide({ label, url }: { label: string; url: string }) {
  return (
    <figure>
      {url ? <img src={url} alt={label} /> : <NoImage>No image</NoImage>}
      <figcaption>{label}</figcaption>
    </figure>
  );
}

function blockerLink(
  blocker: Pick<Blocker, 'entity' | 'id'>,
  courseId: number,
  version: number,
  label = 'Fix',
) {
  const id = String(blocker.id);
  switch (blocker.entity) {
    case 'question':
      return (
        <Link to="/questions/$questionId" params={{ questionId: id }}>
          {label}
        </Link>
      );
    case 'topic':
      return (
        <Link to="/topics/$topicId" params={{ topicId: id }}>
          {label}
        </Link>
      );
    case 'lesson':
      return (
        <Link to="/lessons/$lessonId" params={{ lessonId: id }}>
          {label}
        </Link>
      );
    default:
      return (
        <Link
          to="/courses/$courseId/$version"
          params={{ courseId: String(courseId), version: String(version) }}
        >
          {label}
        </Link>
      );
  }
}

function PublishDialog({
  courseId,
  version,
  title,
  onClose,
}: {
  courseId: number;
  version: number;
  title: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const check = api.adminCourseVersionPublishCheck.useQuery({
    path: { course_id: courseId, version },
  });
  const publish = api.adminPublishCourseVersion.useMutation(undefined, {
    meta: { toast: 'Published' },
    onSuccess: () => {
      invalidateCourseVersions(courseId);
      onClose();
      navigate({
        to: '/courses/$courseId/$version',
        params: { courseId: String(courseId), version: String(version) },
      });
    },
  });
  const blockers = check.data?.blockers ?? [];
  const impact = check.data?.impact ?? [];
  const hidden = check.data?.hidden ?? [];
  return (
    <ConfirmDialog
      open
      title={`Publish ${title} v${version}`}
      confirmLabel={`Publish v${version}`}
      cancelLabel="Back to changes"
      tone="primary"
      pending={publish.isPending || check.isLoading || blockers.length > 0}
      onConfirm={() =>
        publish.mutate({ path: { course_id: courseId, version } })
      }
      onCancel={onClose}
    >
      {check.isLoading ? (
        <p>Checking the draft…</p>
      ) : (
        <>
          <Impact>
            <li>
              <b>→</b>
              <span>
                <strong>New students</strong> start on v{version}, on the web,
                the FullApp and the LiteApp.
              </span>
            </li>
            {impact.map((v) => (
              <li key={v.version}>
                <b>{v.in_progress}</b>
                <span>
                  {v.in_progress === 1 ? 'student' : 'students'} partway through{' '}
                  <strong>v{v.version}</strong> stay on v{v.version} until they
                  finish.
                </span>
              </li>
            ))}
            <li>
              <b>0</b>
              <span>answers, grades or progress change for anyone.</span>
            </li>
          </Impact>
          {blockers.length > 0 && (
            <Blockers role="alert">
              <strong>
                Fix{' '}
                {blockers.length === 1
                  ? '1 thing'
                  : `${blockers.length} things`}{' '}
                before publishing:
              </strong>
              <ul>
                {blockers.map((b) => (
                  <li key={`${b.code}-${b.entity}-${b.id}`}>
                    {b.title ? <em>{b.title}</em> : null} {b.message}{' '}
                    {blockerLink(b, courseId, version)}
                  </li>
                ))}
              </ul>
            </Blockers>
          )}
          {hidden.length > 0 && (
            <Hidden>
              <strong>
                Stays hidden from students after publishing, because it's set to
                draft:
              </strong>
              <ul>
                {hidden.map((h) => (
                  <li key={`${h.entity}-${h.id}`}>
                    <em>{h.title}</em> ({h.entity}){' '}
                    {blockerLink(h, courseId, version, 'Open')}
                  </li>
                ))}
              </ul>
              <p>Publish it first if students should see it in v{version}.</p>
            </Hidden>
          )}
          {publish.isError && (
            <ErrorText>
              {apiErrorDetail(publish.error) ??
                'Could not publish. Nothing went live; please try again.'}
            </ErrorText>
          )}
        </>
      )}
    </ConfirmDialog>
  );
}

const KIND_TONE: Record<ChangeKind, string> = {
  added: 'var(--success)',
  edited: 'var(--orange)',
  moved: 'var(--blue)',
  removed: 'var(--error)',
};

const Heading = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  margin-bottom: 0.5rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
  }

  span {
    color: var(--grey-700);
    font-size: 0.875rem;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: var(--component-margin);
`;

const Chip = styled.span<{ $kind: ChangeKind }>`
  align-items: center;
  background: color-mix(in srgb, ${(p) => KIND_TONE[p.$kind]} 12%, transparent);
  border-radius: 0.25rem;
  color: var(--grey-1000);
  display: inline-flex;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
`;

const Empty = styled.p`
  color: var(--grey-700);
  padding: 1rem 0;
`;

const GroupList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const Group = styled.section`
  background: var(--component-color);
  border: 1px solid var(--grey-200);
  border-radius: 0.375rem;
`;

const GroupTitle = styled.h3`
  background: var(--grey-100);
  border-bottom: 1px solid var(--grey-200);
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
  padding: 0.375rem 0.75rem;
`;

const Row = styled.div`
  border-top: 1px solid var(--grey-150);
  display: grid;
  gap: 0.375rem;
  padding: 0.625rem 0.75rem;

  &:first-of-type {
    border-top: 0;
  }
`;

const RowHead = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
`;

const RowTitle = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  a {
    color: var(--grey-1000);
  }
`;

const Kind = styled.span`
  color: var(--grey-700);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Diff = styled.div`
  background: var(--grey-50);
  border-radius: 0.25rem;
  font-size: 0.875rem;
  line-height: 1.6;
  padding: 0.375rem 0.625rem;

  p {
    margin: 0;
  }

  del {
    background: color-mix(in srgb, var(--error) 14%, transparent);
    color: var(--error);
  }

  ins {
    background: color-mix(in srgb, var(--success) 16%, transparent);
    color: var(--grey-1000);
    text-decoration: none;
  }
`;

const DiffHead = styled.div`
  align-items: baseline;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
`;

// Quiet beside the row's own button: it undoes just one field.
const FieldUndo = styled.button`
  background: none;
  border: 0;
  color: var(--blue);
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
  padding: 0;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: var(--grey-500);
    cursor: default;
  }
`;

const DiffLabel = styled.div`
  color: var(--grey-700);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ImagePair = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 0.375rem;

  figure {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin: 0;
  }

  img {
    background-color: var(--grey-100);
    border: 1px solid var(--grey-200);
    border-radius: 4px;
    display: block;
    height: 90px;
    object-fit: cover;
    width: 160px;
  }

  figcaption {
    color: var(--grey-700);
    font-size: 0.75rem;
  }
`;

const NoImage = styled.div`
  align-items: center;
  border: 1px dashed var(--grey-300);
  border-radius: 4px;
  color: var(--grey-600);
  display: flex;
  font-size: 0.75rem;
  height: 90px;
  justify-content: center;
  width: 160px;
`;

const Arrow = styled.span`
  color: var(--grey-600);
  font-size: 1.25rem;
`;

const Settings = styled.p`
  color: var(--grey-800);
  font-size: 0.8125rem;
  margin: 0;

  b {
    color: var(--grey-1000);
  }
`;

const Unchanged = styled.p`
  color: var(--grey-700);
  font-size: 0.8125rem;
  margin: 0.75rem 0 5rem;
`;

const StickyBar = styled.div`
  align-items: center;
  background: var(--component-color);
  border-top: 1px solid var(--grey-200);
  bottom: 0;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  position: sticky;

  span {
    color: var(--grey-700);
    font-size: 0.8125rem;
  }
`;

const Impact = styled.ul`
  display: grid;
  gap: 0.375rem;
  list-style: none;
  margin: 0 0 0.75rem;
  padding: 0;

  li {
    align-items: baseline;
    display: flex;
    gap: 0.5rem;
  }

  b {
    font-variant-numeric: tabular-nums;
    min-width: 3ch;
    text-align: right;
  }
`;

const Blockers = styled.div`
  background: color-mix(in srgb, var(--error) 8%, transparent);
  border-left: 3px solid var(--error);
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;

  ul {
    margin: 0.25rem 0 0;
    padding-left: 1.125rem;
  }
`;

const Hidden = styled.div`
  background: var(--grey-50);
  border-left: 3px solid var(--grey-400);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;

  ul {
    margin: 0.25rem 0;
    padding-left: 1.125rem;
  }

  p {
    color: var(--grey-700);
    margin: 0;
  }
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
